
import { getCurrentClerkUserId } from "@/integrations/supabase/client";

// Input validation and sanitization utilities
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  // Remove potential XSS vectors
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s-()]{10,}$/;
  return phoneRegex.test(phone);
};

export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Authentication validation
export const validateUserAuthentication = (): { isValid: boolean; userId: string | null } => {
  const userId = getCurrentClerkUserId();
  
  return {
    isValid: !!userId,
    userId
  };
};

// Content security validation
export const validateEventContent = (title: string, description?: string): string[] => {
  const errors: string[] = [];
  
  if (!title || title.trim().length === 0) {
    errors.push('Title is required');
  }
  
  if (title && title.length > 200) {
    errors.push('Title must be less than 200 characters');
  }
  
  if (description && description.length > 5000) {
    errors.push('Description must be less than 5000 characters');
  }
  
  return errors;
};

// Rate limiting helpers (client-side)
const rateLimitMap = new Map<string, { count: number; lastReset: number }>();

export const checkRateLimit = (action: string, maxRequests: number = 10, windowMs: number = 60000): boolean => {
  const userId = getCurrentClerkUserId();
  if (!userId) return false;
  
  const key = `${userId}-${action}`;
  const now = Date.now();
  const record = rateLimitMap.get(key);
  
  if (!record || now - record.lastReset > windowMs) {
    rateLimitMap.set(key, { count: 1, lastReset: now });
    return true;
  }
  
  if (record.count >= maxRequests) {
    return false;
  }
  
  record.count++;
  return true;
};

// Security logging (for client-side events)
export const logSecurityEvent = (event: string, details?: any) => {
  const userId = getCurrentClerkUserId();
  
  console.log(`[SECURITY] ${event}`, {
    userId,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    userAgent: navigator.userAgent,
    details
  });
  
  // Log to Supabase security_logs table for monitoring
  if (userId) {
    // Use dynamic import to avoid circular dependencies
    import('@/integrations/supabase/client').then(({ supabase }) => {
      supabase.from('security_logs').insert({
        user_id: userId,
        action: event,
        details: details || {},
        success: true,
        ip_address: '127.0.0.1', // Would be actual IP in production
        user_agent: navigator.userAgent
      }).then(({ error }) => {
        if (error) {
          console.warn('[SECURITY] Failed to log security event:', error);
        }
      });
    }).catch(() => {
      // Silently fail if logging module can't be loaded
    });
  }
};

// Content Security Policy helpers
export const setSecurityHeaders = () => {
  // This would typically be done at the server level
  // but we can add some client-side security measures
  
  // Prevent framing (clickjacking protection)
  if (window.self !== window.top) {
    console.warn('[SECURITY] Page loaded in frame - potential clickjacking attempt');
    logSecurityEvent('FRAME_DETECTED');
  }
  
  // Check for secure context
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
    console.warn('[SECURITY] Page not served over HTTPS');
    logSecurityEvent('INSECURE_CONNECTION');
  }
};

// Initialize security measures
export const initializeSecurity = () => {
  setSecurityHeaders();
  
  // Add global error handler for security events
  window.addEventListener('error', (event) => {
    if (event.error?.name === 'SecurityError') {
      logSecurityEvent('SECURITY_ERROR', {
        message: event.error.message,
        filename: event.filename,
        lineno: event.lineno
      });
    }
  });
};
